import { Extension } from '@tiptap/core'
import { Mark } from '@tiptap/pm/model'
import { Plugin } from '@tiptap/pm/state'

type ToolNameOptions = {
  resolveToolNameById?: (toolId: string) => string | null | undefined
  toolBackgroundColor?: string
}

type ToolReplacement = {
  from: number
  to: number
  text: string
  marks: readonly Mark[]
}

const TOOL_MARK_NAME = 'slash-command-pill'
const TOOL_ID_ATTRIBUTE = 'data-toolid'
const TOOL_DATA_TYPE = 'tool'
const RESOLVED_TOOL_BACKGROUND_COLOR = '#E5D5FF'
const MISSING_TOOL_BACKGROUND_COLOR = '#E5E7EB'
export const TOOL_NAME_RESOLVED_META_KEY = 'toolNameResolved'
export const TOOL_NAME_REFRESH_META_KEY = 'toolNameRefresh'

const areMarkAttributesEqual = (
  previousAttrs: Record<string, unknown>,
  nextAttrs: Record<string, unknown>,
) => {
  const previousKeys = Object.keys(previousAttrs)
  const nextKeys = Object.keys(nextAttrs)

  if (previousKeys.length !== nextKeys.length) {
    return false
  }

  return previousKeys.every((key) => previousAttrs[key] === nextAttrs[key])
}

export const ToolName = Extension.create<ToolNameOptions>({
  name: 'toolName',

  addOptions() {
    return {
      resolveToolNameById: undefined,
      toolBackgroundColor: undefined,
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction: (transactions, _oldState, newState) => {
          const hasToolNameHydrationWrite = transactions.some((transaction) => {
            return Boolean(transaction.getMeta(TOOL_NAME_RESOLVED_META_KEY))
          })
          if (hasToolNameHydrationWrite) {
            return null
          }

          const hasDocChanges = transactions.some((transaction) => {
            return transaction.docChanged
          })
          const hasRefreshRequest = transactions.some((transaction) => {
            return Boolean(transaction.getMeta(TOOL_NAME_REFRESH_META_KEY))
          })

          if (!hasDocChanges && !hasRefreshRequest) {
            return null
          }

          const resolveToolNameById = this.options.resolveToolNameById

          if (!resolveToolNameById) {
            return null
          }

          const replacements: ToolReplacement[] = []

          // Scan text nodes and queue tool mark rewrites for both text + background state.
          newState.doc.descendants((node, pos) => {
            if (!node.isText || !node.text) {
              return
            }

            const toolMark = node.marks.find((mark) => {
              if (mark.type.name !== TOOL_MARK_NAME) {
                return false
              }

              const dataType = mark.attrs?.['data-type']
              const toolId = mark.attrs?.[TOOL_ID_ATTRIBUTE]

              // Accept explicit tool pills, or any slash-command pill carrying a tool id.
              return dataType === TOOL_DATA_TYPE || Boolean(toolId)
            })

            if (!toolMark) {
              return
            }

            const toolId = toolMark.attrs?.[TOOL_ID_ATTRIBUTE]

            if (typeof toolId !== 'string' || toolId.trim() === '') {
              return
            }

            const resolvedName = resolveToolNameById(toolId)?.trim()
            const nextBackgroundColor =
              this.options.toolBackgroundColor ||
              (resolvedName
                ? RESOLVED_TOOL_BACKGROUND_COLOR
                : MISSING_TOOL_BACKGROUND_COLOR)
            const nextText = resolvedName || node.text
            const nextMarks = node.marks.map((mark) => {
              if (mark !== toolMark) {
                return mark
              }
              const nextToolMarkAttrs = {
                ...mark.attrs,
                color: nextBackgroundColor,
              }
              return mark.type.create(nextToolMarkAttrs)
            })

            const didTextChange = nextText !== node.text
            const didToolMarkAttributesChange = !areMarkAttributesEqual(
              toolMark.attrs || {},
              {
                ...toolMark.attrs,
                color: nextBackgroundColor,
              },
            )
            if (!didTextChange && !didToolMarkAttributesChange) {
              return
            }

            replacements.push({
              from: pos,
              to: pos + node.text.length,
              text: nextText,
              marks: nextMarks,
            })
          })

          if (!replacements.length) {
            return null
          }

          // Apply in reverse to keep positions valid as replacement lengths vary.
          let tr = newState.tr
          replacements
            .sort((a, b) => b.from - a.from)
            .forEach((replacement) => {
              tr = tr.replaceWith(
                replacement.from,
                replacement.to,
                newState.schema.text(replacement.text, replacement.marks),
              )
            })

          // Signal downstream plugins that this is an internal hydration rewrite.
          tr = tr.setMeta(TOOL_NAME_RESOLVED_META_KEY, true)

          return tr.docChanged ? tr : null
        },
      }),
    ]
  },
})
